import { formatSlideCount } from './label-util';

/**
 * Contains definitions for all metrics to be recorded in Prometheus. Any metric that is timed using
 * the ActivityProfiler must be defined here.
 *
 * !!WARNING!!: Do not change any existing definitions unless you are ok with potentially invalidating
 * all previous observations for the same metric.
 *
 * @example
 * A metric definition is an object with the following shape:
 *
 * interface labelMappingInterface = {
 *   [key: string]: string[]
 * }
 *
 * declare function getLabelsFct(...args): labelMappingInterface
 *
 * interface metric =
 * {
 *  name:string
 *  description:string
 *  type:string
 *  labelValueMapping:labelMappingInterface // a key value pairing of label names to valid label values. The object
 *      // key represents a unique label name and the value is a finite set of unique values that label can have.
 *      // Observations for that metric will be validated to ensure all labels recorded here are provided a value
 *      // from its unique value set.
 *  histogramBuckets:number[]? // if the metric type is a histogram, this is a list of integers
 *  getLabels?: getLabelsFct // a helper method to transform arguments into the expected label set. Recommended
 *      to implement for consistency
 * }
 */

export type Metric<T extends string | number | void> = {
    name: string; // see https://prometheus.io/docs/practices/naming/#metric-names
    description: string; // a user friendly explanation of what's being observed
    type: 'counter' | 'gauge' | 'histogram'; // a prometheus metric type compatible with promjs
    // labelValueMapping is a key value pairing of label names to valid label values. The object
    // key represents a unique label name and the value is a finite set of unique values that label can have.
    // Observations for that metric will be validated to ensure all labels recorded here are provided a value
    // from its unique value set.
    labelValueMapping: Record<string, string[]>;
    histogramBuckets?: number[]; // if the metric type is a histogram, this is a list of integers
    // getLabels is a helper method to transform arguments into the expected label set. Recommended
    // to implement for consistency
    getLabels: (arg: T) => Record<string, string>;
};

export type AnyMetric = Metric<string> | Metric<number> | Metric<void>;

// timing buckets for operations which will generate new scene previews for the entire video
const VIDEO_OPERATION_TIMING_BUCKETS_MS = [1000, 2500, 5000, 10000, 25000, 60000];

export const METRIC_LOAD_CREATOR_NAME = 'app_load_creator_ms_total';
export const METRIC_LOAD_CREATOR: Metric<string> = {
    name: METRIC_LOAD_CREATOR_NAME,
    description: 'Time from selecting a format until the creator has loaded',
    type: 'histogram',
    histogramBuckets: VIDEO_OPERATION_TIMING_BUCKETS_MS,
    labelValueMapping: {
        action: ['new', 'edit', 'instant'],
    },
    getLabels: function (action: string) {
        return {
            action,
        };
    },
};

export enum VALID_SLIDE_COUNTS {
    ONE_TO_TEN = '1-10',
    ELEVEN_TO_TWNETY = '11-20',
    TWENTY_ONE_TO_THIRTY = '21-30',
    THIRTY_ONE_TO_FORTY = '31-40',
    FORTY_PLUS = '40+',
}

export const METRIC_CHANGE_ASPECT_RATIO_NAME = 'app_change_aspect_ratio_duration_ms_total';
export const METRIC_CHANGE_ASPECT_RATIO: Metric<number> = {
    name: METRIC_CHANGE_ASPECT_RATIO_NAME,
    description:
        'Time from when aspect ratio is changed until all scene previews have been updated',
    type: 'histogram',
    histogramBuckets: VIDEO_OPERATION_TIMING_BUCKETS_MS,
    labelValueMapping: {
        slideCount: Object.values(VALID_SLIDE_COUNTS),
    },
    getLabels: function (slideCount: number) {
        return {
            slideCount: formatSlideCount(slideCount),
        };
    },
};

export const METRIC_UNDO_REDO_NAME = 'app_undo_redo_duration_ms_total';
export const METRIC_UNDO_REDO: Metric<number> = {
    name: METRIC_UNDO_REDO_NAME,
    description:
        'Time from when undo or redo is clicked until all scene previews have been updated',
    type: 'histogram',
    histogramBuckets: VIDEO_OPERATION_TIMING_BUCKETS_MS,
    labelValueMapping: {
        slideCount: Object.values(VALID_SLIDE_COUNTS),
    },
    getLabels: function (slideCount: number) {
        return {
            slideCount: formatSlideCount(slideCount),
        };
    },
};

export const METRIC_INITIAL_VIDEO_LOAD_NAME = 'app_initial_video_load_duration_ms_total';
export const METRIC_INITIAL_VIDEO_LOAD: Metric<number> = {
    name: METRIC_INITIAL_VIDEO_LOAD_NAME,
    description:
        'Time from when after we have fetched everything and start loading the video until all scene previews are ready',
    type: 'histogram',
    histogramBuckets: VIDEO_OPERATION_TIMING_BUCKETS_MS,
    labelValueMapping: {
        slideCount: Object.values(VALID_SLIDE_COUNTS),
    },
    getLabels: function (slideCount: number) {
        return {
            slideCount: formatSlideCount(slideCount),
        };
    },
};

export const METRIC_SCRIPT_OPTIONS_LOAD_NAME = 'app_script_options_load_duration_ms_total';
export const METRIC_SCRIPT_OPTIONS_LOAD: Metric<void> = {
    name: METRIC_SCRIPT_OPTIONS_LOAD_NAME,
    description:
        'Time from when script options start being fetched until they have all been received',
    type: 'histogram',
    histogramBuckets: VIDEO_OPERATION_TIMING_BUCKETS_MS,
    labelValueMapping: {},
    getLabels: function () {
        return {};
    },
};

export const METRIC_SCRIPT_LOAD_NAME = 'app_script_load_duration_ms_total';
export const METRIC_SCRIPT_LOAD: Metric<void> = {
    name: METRIC_SCRIPT_LOAD_NAME,
    description: 'Time to receive entire generated script',
    type: 'histogram',
    histogramBuckets: VIDEO_OPERATION_TIMING_BUCKETS_MS,
    labelValueMapping: {},
    getLabels: function () {
        return {};
    },
};

export const METRIC_UPLOAD_STARTED_NAME = 'app_upload_started_count';
export const METRIC_UPLOAD_STARTED: Metric<void> = {
    name: METRIC_UPLOAD_STARTED_NAME,
    description: 'Number of uploads started',
    type: 'counter',
    labelValueMapping: {},
    getLabels: function () {
        return {};
    },
};

export const METRIC_UPLOAD_SUCCESS_NAME = 'app_upload_success_count';
export const METRIC_UPLOAD_SUCCESS: Metric<void> = {
    name: METRIC_UPLOAD_SUCCESS_NAME,
    description: 'Number of uploads successully completed',
    type: 'counter',
    labelValueMapping: {},
    getLabels: function () {
        return {};
    },
};

export const METRIC_UPLOAD_FAILED_NAME = 'app_upload_failed_count';
export const METRIC_UPLOAD_FAILED: Metric<void> = {
    name: METRIC_UPLOAD_FAILED_NAME,
    description: 'Number of uploads failed',
    type: 'counter',
    labelValueMapping: {},
    getLabels: function () {
        return {};
    },
};

export const OTHER_PATH = 'other-path';

export const WHITELISTED_METRICS_PATHS = [
    '/auth',
    '/app',
    '/dashboard',
    '/pricing',
    '/user',
    '/',
    OTHER_PATH,
];

export const METRIC_LCP_NAME = 'web_lcp_seconds';
export const METRIC_LCP: Metric<string> = {
    name: METRIC_LCP_NAME,
    description: 'Largest Contentful Paint in seconds',
    type: 'histogram',
    histogramBuckets: [0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0, 10.0],
    labelValueMapping: {
        path: WHITELISTED_METRICS_PATHS,
    },
    getLabels: function (path: string) {
        return { path };
    },
};

export const METRIC_CLS_NAME = 'web_cls_score';
export const METRIC_CLS: Metric<string> = {
    name: METRIC_CLS_NAME,
    description: 'Cumulative Layout Shift score',
    type: 'histogram',
    histogramBuckets: [0.0, 0.1, 0.25, 0.5, 0.75, 1.0, 2.0],
    labelValueMapping: {
        path: WHITELISTED_METRICS_PATHS,
    },
    getLabels: function (path: string) {
        return { path };
    },
};

export const METRICS_TO_RECORD = [
    METRIC_LOAD_CREATOR,
    METRIC_CHANGE_ASPECT_RATIO,
    METRIC_UNDO_REDO,
    METRIC_INITIAL_VIDEO_LOAD,
    METRIC_SCRIPT_OPTIONS_LOAD,
    METRIC_SCRIPT_LOAD,
    METRIC_UPLOAD_STARTED,
    METRIC_UPLOAD_SUCCESS,
    METRIC_UPLOAD_FAILED,
    METRIC_LCP,
    METRIC_CLS,
];

export const METRIC_NAME_MAPPING: Record<string, AnyMetric> = {
    [METRIC_LOAD_CREATOR_NAME]: METRIC_LOAD_CREATOR,
    [METRIC_CHANGE_ASPECT_RATIO_NAME]: METRIC_CHANGE_ASPECT_RATIO,
    [METRIC_UNDO_REDO_NAME]: METRIC_UNDO_REDO,
    [METRIC_INITIAL_VIDEO_LOAD_NAME]: METRIC_INITIAL_VIDEO_LOAD,
    [METRIC_SCRIPT_OPTIONS_LOAD_NAME]: METRIC_SCRIPT_OPTIONS_LOAD,
    [METRIC_SCRIPT_LOAD_NAME]: METRIC_SCRIPT_LOAD,
    [METRIC_UPLOAD_STARTED_NAME]: METRIC_UPLOAD_STARTED,
    [METRIC_UPLOAD_SUCCESS_NAME]: METRIC_UPLOAD_SUCCESS,
    [METRIC_UPLOAD_FAILED_NAME]: METRIC_UPLOAD_FAILED,
    [METRIC_LCP_NAME]: METRIC_LCP,
    [METRIC_CLS_NAME]: METRIC_CLS,
};
